import {
  Directive,
  Renderer2,
  ChangeDetectorRef,
  AfterContentInit,
  Input,
  Self,
  Optional,
  ContentChild,
  ViewChild,
  EventEmitter,
  Output,
  AfterViewInit,
  ElementRef,
  OnDestroy,
} from "@angular/core";
import { NgControl, ControlValueAccessor } from "@angular/forms";

import { coerceBooleanProperty, BooleanInput } from "@vattenfall/util";

import { Subscription, ReplaySubject } from "rxjs";

import { InputLabelDirective, InputHintDirective, InputMessageDirective } from "./inputs";

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class InputValueAccessor<T, E extends HTMLInputElement | HTMLSelectElement>
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  /**
   * Event emitted when the input value changes.
   */
  @Output() readonly change: EventEmitter<T | T[]> = new EventEmitter<T | T[]>();
  /**
   * Whether the input is pending (before view has been initialized)
   */
  get isPending() {
    return this._isPending;
  }
  /**
   * Whether the input is ready (after view has been initialized)
   */
  get isReady() {
    return !this.isPending;
  }
  /**
   * Whether the input is valid
   */
  get isValid() {
    if (this._isNgControl) return this.ngControl.valid;
    return this.inputElement?.nativeElement?.checkValidity();
  }
  /**
   * Whether the input is invalid
   */
  get isInvalid() {
    return !this.isValid;
  }
  /**
   * Whether the input is disabled
   */
  get isDisabled() {
    if (this._isNgControl) return this.ngControl.disabled;
    return this.inputElement?.nativeElement?.disabled;
  }
  /**
   * Whether the input is enabled
   */
  get isEnabled() {
    return !this.isDisabled;
  }
  /**
   * Whether the input is dirty
   */
  get isDirty() {
    if (this._isNgControl) return this.ngControl.dirty;
    return this._isDirty;
  }
  /**
   * Whether the input is pristine
   */
  get isPristine() {
    return !this.isDirty;
  }
  /**
   * Whether the input is touched
   */
  get isTouched() {
    if (this._isNgControl) return this.ngControl.touched;
    return this._isTouched;
  }
  /**
   * Whether the input is untouched
   */
  get isUntouched() {
    return !this.isTouched;
  }
  /**
   * Helper method that sets a property on a target element using the current Renderer implementation.
   */
  protected setProperty(key: string, value: unknown): void {
    this._setPropertySubject.next([key, value]);
  }

  @ViewChild("inputElement", { read: ElementRef }) inputElement!: ElementRef<E>;

  private _isNgControl = false;
  private _isPending = true;
  private _isDirty = false;
  private _isTouched = false;

  private _setPropertySubject = new ReplaySubject<[string, unknown]>(Number.POSITIVE_INFINITY);
  private _setPropertySubscription = Subscription.EMPTY;

  constructor(public ngControl: NgControl, protected renderer: Renderer2, public changeDetectorRef: ChangeDetectorRef) {
    if (this.ngControl) {
      this._isNgControl = true;
      // Allows `<ui-input>` component to register as a `ControlValueAccessor`.
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit() {
    this._isPending = false;
    this._setPropertySubscription = this._setPropertySubject.subscribe(([key, value = null]) => {
      this.renderer.setProperty(this.inputElement.nativeElement, key, value);
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this._setPropertySubject.complete();
    this._setPropertySubscription.unsubscribe();
  }

  /**
   * The registered callback function called when a change or input event occurs on the input
   * element.
   */
  onChange(value: T | T[]) {
    if (this._isNgControl) {
      this.ngControl.control?.setValue(value);
      this.ngControl.control?.markAsDirty();
    }
    this._isDirty = true;
    this.change.emit(value);
  }
  /**
   * The registered callback function called when a blur event occurs on the input element.
   */
  onTouched() {
    if (this._isNgControl) {
      this.ngControl.control?.markAsTouched();
    }
    this._isTouched = true;
  }
  /**
   * Writes a new value to the element.
   */
  writeValue(value: T): void {
    this.setProperty("value", value);
  }
  /**
   * Registers a function called when the control is touched.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  /**
   * Registers a function called when the control value changes.
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnChange(fn: (_: unknown) => {}): void {
    this.onChange = fn;
  }
  /**
   * Sets the "disabled" property on the range input element.
   */
  setDisabledState(disabled: boolean): void {
    this.setProperty("disabled", disabled);
  }
}

/**
 * Increasing integer for generating unique ids.
 */
let nextUniqueId = 0;

/**
 * The `InputsElementAccessor` is the access layer to the `InputValueAccessor`.
 *
 * It handles attributes and content projection that is shared across all type of inputs, such as `name`, `id` and `required`.
 * The `InputsElementAccessor` require a generic type to be specified, this type should correspond to the value emitted on input change.
 */
@Directive()
export class InputsElementAccessor<T, E extends HTMLInputElement | HTMLSelectElement>
  extends InputValueAccessor<T, E>
  implements AfterContentInit
{
  /**
   * Type of form control.
   *
   * Default is `text`.
   */
  @Input() type:
    | "text"
    | "textarea"
    | "tel"
    | "email"
    | "number"
    | "checkbox"
    | "radio"
    | "switch"
    | "select"
    | "option"
    | "option-group"
    | "date"
    | "month"
    | "year"
    | "week" = "text";
  /**
   * Label for input element. Behaves the same as `<ui-input-label>` element.
   */
  @Input() label: string | null = null;
  /**
   * Text that hints the input element usage. Behaves the same as `<ui-input-hint>` element.
   */
  @Input()
  get hint(): string | null {
    return this.isUntouched || (this.isValid && this._hint) ? this._hint : null;
  }
  set hint(value: string | null) {
    this._hint = value;
  }
  private _hint: string | null = null;
  /**
   * Text to display when the input element is invalid. Behaves the same as `<ui-input-error>` element.
   */
  @Input()
  get error(): string | null {
    return this.isTouched && this.isInvalid && this._error ? this._error : null;
  }
  set error(value: string | null) {
    this._error = value;
  }
  private _error: string | null = null;
  /**
   * Whether the checkbox is disabled.
   *
   * Default is `false`.
   */
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this.setProperty("disabled", this._disabled);
    if (this.ngControl)
      if (this._disabled) this.ngControl.control?.disable();
      else this.ngControl.control?.enable();
  }
  private _disabled = false;
  /**
   * Whether the checkbox is required.
   *
   * Default is `false`.
   */
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.setProperty("required", this._required);
  }
  private _required = false;
  /**
   * A unique id for the checkbox input. If none is supplied, it will be auto-generated.
   */
  @Input()
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value;
    this.setProperty("id", this._id);
  }
  private _id = `ui-input-${nextUniqueId++}`;
  /**
   * Name value will be applied to the input element if present.
   */
  @Input()
  get name(): string | null {
    return this._name;
  }
  set name(value: string | null) {
    this._name = value;
    this.setProperty("name", this._name);
  }
  private _name: string | null = null;
  /**
   * Whether the control should display valid and invalid states.
   *
   * Default is `false`.
   */
  @Input()
  get validate(): boolean {
    return this._validate;
  }
  set validate(value: BooleanInput) {
    this._validate = coerceBooleanProperty(value);
  }
  private _validate = false;

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
    protected renderer: Renderer2,
    public changeDetectorRef: ChangeDetectorRef
  ) {
    super(ngControl, renderer, changeDetectorRef);
  }

  ngAfterContentInit() {
    if (this.inputLabelDirective) this.label = this.inputLabelDirective.elementRef.nativeElement.textContent;
    if (this.inputHintDirective) this.hint = this.inputHintDirective.elementRef.nativeElement.textContent;
    if (this.inputMessageDirective) this.error = this.inputMessageDirective.elementRef.nativeElement.textContent;
  }

  @ContentChild(InputLabelDirective) protected inputLabelDirective?: InputLabelDirective;
  @ContentChild(InputHintDirective) protected inputHintDirective?: InputHintDirective;
  @ContentChild(InputMessageDirective) protected inputMessageDirective?: InputMessageDirective;
}
